import { useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Select from 'components/UI/Select';
import { ReactComponent as InfoIcon } from 'assets/icons/digital-loan-info.svg';
import { MOCK_PRODUCT_CONDITION_OPTIONS } from 'modules/digitalLoan/mocks';
import {
  MANUAL_NAME_MAX_LENGTH,
  OTHER_OPTION_ID,
} from 'constants/digitalLoanPhase2';
import { formatMoneyInput, parseMoneyInput } from 'routes/DigitalLoan/utils/moneyInput';
import {
  isUiOtherOption,
  withManualOtherOption,
} from 'routes/DigitalLoan/utils/catalogOtherOption';

const ModelDetailScreen = ({
  literals,
  models,
  selectedModelId,
  manualModelName = '',
  allowManualModel = false,
  productCondition,
  estimatedValue,
  estimatedValueLocked = false,
  onModelChange,
  onManualModelChange,
  onConditionChange,
  onEstimatedValueChange,
  onNext,
  onBack,
  loading = false,
}) => {
  const isEmptyCatalogManual = allowManualModel && !loading;
  const isOtherSelected = selectedModelId === OTHER_OPTION_ID;
  const showSelect = !isEmptyCatalogManual;
  const showManualInput = isEmptyCatalogManual || isOtherSelected;

  const modelOptions = useMemo(() => {
    const catalogOptions = models.map((model) => ({
      value: model.id,
      label: model.label || model.name,
    }));
    if (!showSelect) return catalogOptions;
    return withManualOtherOption(models, {
      name: literals.otherOption || 'OTRO',
    }).map((model) => ({
      value: isUiOtherOption(model) ? OTHER_OPTION_ID : model.id,
      label: model.label || model.name,
    }));
  }, [models, showSelect, literals.otherOption]);

  const conditionOptions = useMemo(() => (
    MOCK_PRODUCT_CONDITION_OPTIONS.map((option) => ({
      value: option.value,
      label: literals.conditions[option.labelKey],
    }))
  ), [literals.conditions]);

  const selectedCatalogModel = useMemo(
    () => models.find((model) => model.id === selectedModelId) || null,
    [models, selectedModelId],
  );

  const descriptionText = selectedCatalogModel?.description
    || selectedCatalogModel?.raw?.DESCRIPCION
    || '';
  const showDescription = showSelect
    && !isOtherSelected
    && !!selectedModelId
    && !!String(descriptionText).trim();

  const hasModel = showManualInput
    ? !!String(manualModelName).trim()
    : !!selectedModelId;

  const hasEstimatedValue = estimatedValue !== ''
    && estimatedValue != null
    && Number.isFinite(Number(estimatedValue))
    && Number(estimatedValue) > 0;

  const showEstimatedValue = isEmptyCatalogManual || isOtherSelected;
  const canContinue = hasModel
    && !!productCondition
    && (!showEstimatedValue || hasEstimatedValue)
    && !loading;

  const selectClassName = showDescription || showManualInput ? 'mb-2' : 'mb-4';

  return (
    <div className='full-screen-container digital-loan-model-detail'>
      <div className='screen-content'>
        <h1 className='digital-loan-phase2__title'>{literals.title}</h1>
        <p className='digital-loan-phase2__body'>{literals.intro1}</p>
        <p className='digital-loan-phase2__body mb-4'>{literals.intro2}</p>

        {showSelect && (
          <Select
            className={selectClassName}
            name='model'
            label={literals.modelLabel}
            placeholder={literals.modelPlaceholder}
            options={modelOptions}
            value={selectedModelId}
            onChange={onModelChange}
            loading={loading}
            required
          />
        )}

        {showDescription && (
          <div className='digital-loan-model-detail__description mb-4'>
            <p className='digital-loan-model-detail__description-label'>
              {literals.descriptionLabel}
            </p>
            <p className='digital-loan-model-detail__description-text'>
              {descriptionText}
            </p>
          </div>
        )}

        {showManualInput && (
          <Input
            className='mb-4'
            name='model'
            label={isEmptyCatalogManual ? literals.modelLabel : literals.manualModelLabel}
            placeholder={literals.modelPlaceholder}
            value={manualModelName}
            onChange={onManualModelChange}
            maxLength={MANUAL_NAME_MAX_LENGTH}
            required
          />
        )}

        <Select
          className='mb-4'
          name='condition'
          label={literals.conditionLabel}
          placeholder={literals.conditionPlaceholder}
          options={conditionOptions}
          value={productCondition}
          onChange={onConditionChange}
          required
        />
        {showEstimatedValue && (
          <>
            <Input
              className='mb-1'
              name='estimatedValue'
              label={literals.estimatedValueLabel}
              placeholder={literals.estimatedValuePlaceholder}
              value={formatMoneyInput(estimatedValue)}
              onChange={(raw) => {
                if (estimatedValueLocked) return;
                onEstimatedValueChange(parseMoneyInput(raw));
              }}
              inputMode='numeric'
              disabled={estimatedValueLocked}
              required
            />
            <p className='digital-loan-model-detail__hint'>
              <InfoIcon
                className='digital-loan-model-detail__hint-icon'
                aria-hidden='true'
              />
              <span>{literals.estimatedValueHint}</span>
            </p>
          </>
        )}
      </div>
      <div className='screen-actions'>
        <Button text={literals.next} disabled={!canContinue} onClick={onNext} />
        <Button text={literals.back} type='secondary' onClick={onBack} />
      </div>
    </div>
  );
};

ModelDetailScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  models: PropTypes.array.isRequired,
  selectedModelId: PropTypes.string,
  manualModelName: PropTypes.string,
  allowManualModel: PropTypes.bool,
  productCondition: PropTypes.string,
  estimatedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  estimatedValueLocked: PropTypes.bool,
  onModelChange: PropTypes.func.isRequired,
  onManualModelChange: PropTypes.func,
  onConditionChange: PropTypes.func.isRequired,
  onEstimatedValueChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default ModelDetailScreen;
